/**
 * EventStreamService - Socket.IO realtime service for live community updates.
 *
 * Connect flow: POST /api/negotiate → socket.io-client handshake with JWT auth →
 * join tenant + user rooms on the server → receive envelope events on `message`.
 */
import { io, Socket } from 'socket.io-client';
import API_BASE_URL from '../config';
import eventEmitter, { EVENTS } from './eventService';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { getAccessToken } from '../utils/tokenStorage';
import { debugLog } from '../utils/debugLog';
import { noteHazardResolved } from './hazardResolvedStamp';

interface EventStreamEvent {
  type: string;
  data: any;
  timestamp: string;
}

interface EventStreamEnvelope extends EventStreamEvent {
  tenantSubdomain?: string;
  tenantId?: string;
  eventId?: string;
}

interface NegotiateResponse {
  type?: string;
  clientUrl: string;
  hub?: string;
  accessToken?: string;
  tenant?: string;
  tenantId?: string;
  tenantSubdomain?: string;
}

class EventStreamService {
  private socket: Socket | null = null;
  private isConnected: boolean = false;
  private reconnectAttempts: number = 0;
  /** Set after a live disconnect so the first `connect` does not catch up. */
  private sawDisconnect = false;
  private maxReconnectAttempts: number = 10;
  private reconnectInterval: number = 1000; // Start with 1 second
  private maxReconnectInterval: number = 30000; // Max 30 seconds
  private reconnectTimer: NodeJS.Timeout | null = null;
  private isInitialized: boolean = false;
  private connectionListeners: Array<(connected: boolean) => void> = [];
  private isConnecting: boolean = false;
  
  // Event deduplication tracking (eventType -> eventId -> processedAt ms)
  private processedEvents: Map<string, Map<string, number>> = new Map();
  private eventRetentionTime: number = 60000; // 1 minute retention for deduplication
  
  // Heartbeat mechanism for connection health monitoring
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private heartbeatIntervalMs: number = 30000; // 30 seconds
  private lastHeartbeatResponse: number = 0;
  private heartbeatTimeout: number = 10000; // 10 seconds timeout for heartbeat response
  private expectedTenantSubdomain: string | null = null;
  private expectedTenantId: string | null = null;
  
  // Detect browser for diagnostics (Socket.IO handles transport selection)
  private detectBrowser(): { name: string; isChrome: boolean; version: string } {
    const userAgent = navigator.userAgent;
    const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);

    return {
      name: isChrome ? 'Chrome' : 'Other',
      isChrome,
      version: 'Unknown',
    };
  }

  // Action tracking for sync status management (deprecated - using HTTP APIs now)
  private pendingActions: Map<string, { actionType: string; timestamp: number; resolve?: (value: any) => void; reject?: (error: any) => void }> = new Map();
  private actionTimeout: number = 30000; // 30 seconds timeout for actions

  constructor() {
    this.setupOnlineOfflineListeners();
    this.startEventCleanup();
  }

  // Clean up processed events older than eventRetentionTime
  private startEventCleanup(): void {
    setInterval(() => {
      const cutoff = Date.now() - this.eventRetentionTime;
      this.processedEvents.forEach((eventIds, eventType) => {
        let removed = 0;
        eventIds.forEach((processedAt, eventId) => {
          if (processedAt < cutoff) {
            eventIds.delete(eventId);
            removed++;
          }
        });
        if (removed > 0) {
          console.log(`🧹 EventStreamService: Expired ${removed} processed events for type ${eventType}`);
        }
        // Safety cap: clear all if map grows unexpectedly large
        if (eventIds.size > 200) {
          console.log(`🧹 EventStreamService: Clearing ${eventIds.size} processed events for type ${eventType} (size cap)`);
          this.processedEvents.set(eventType, new Map());
        }
      });
    }, 30000); // Clean up every 30 seconds
  }

  // Start heartbeat mechanism to monitor connection health
  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.connected) {
        const now = Date.now();
        this.lastHeartbeatResponse = now;

        try {
          this.socket.emit('ping', { timestamp: now });

          setTimeout(() => {
            if (this.lastHeartbeatResponse === now) {
              this.handleConnectionIssue('Heartbeat timeout');
            }
          }, this.heartbeatTimeout);
        } catch (error) {
          this.handleConnectionIssue('Heartbeat ping failed');
        }
      }
    }, this.heartbeatIntervalMs);
  }

  // Stop heartbeat mechanism
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // Handle connection issues detected by heartbeat
  private handleConnectionIssue(reason: string): void {
    // Force reconnection
    this.disconnect();
    this.scheduleReconnect();
  }

  // Check if event has been processed recently to prevent duplicates
  private isEventDuplicate(eventType: string, eventId: string): boolean {
    if (!this.processedEvents.has(eventType)) {
      this.processedEvents.set(eventType, new Map());
    }

    const eventIds = this.processedEvents.get(eventType)!;
    const processedAt = eventIds.get(eventId);
    if (processedAt !== undefined && Date.now() - processedAt < this.eventRetentionTime) {
      return true; // Event already processed within retention window
    }

    eventIds.set(eventId, Date.now());
    return false;
  }

  // Generate a unique event ID for deduplication
  private generateEventId(eventType: string, data: any, envelopeEventId?: string): string {
    if (envelopeEventId) {
      return String(envelopeEventId);
    }
    if (data?.eventId) {
      return String(data.eventId);
    }
    // For hazard updates, include resolved status and timestamp to make each update unique
    if (eventType === 'hazard_updated' && data.id) {
      // Stable id for dedupe: same payload (e.g. realtime retry or poll+push race) is skipped once.
      const resolvedStatus = data.resolved !== undefined ? `r:${data.resolved}` : '';
      const desc =
        typeof data.description === 'string' ? data.description.slice(0, 96) : '';
      let fingerprint = '';
      try {
        const parts: string[] = [resolvedStatus, `d:${desc}`];
        if (data.comment_count !== undefined) {
          parts.push(`cc:${data.comment_count}`);
        }
        if (data.latitude !== undefined || data.longitude !== undefined) {
          parts.push(`loc:${data.latitude},${data.longitude}`);
        }
        if (data.username !== undefined) {
          parts.push(`u:${data.username}`);
        }
        const utcTs = data.utc_timestamp ?? data.time2;
        if (utcTs !== undefined) {
          parts.push(`ts:${utcTs}`);
        }
        if (Array.isArray(data.tags)) {
          const tagKey = [...data.tags]
            .map((t: unknown) => (typeof t === 'string' ? t : String(t)))
            .sort()
            .join(',');
          parts.push(`tags:${tagKey}`);
        }
        if (Array.isArray(data.image_data)) {
          parts.push(`img:${JSON.stringify(data.image_data).slice(0, 200)}`);
        }
        if (Array.isArray(data.media) && data.media.length > 0) {
          const m0 = data.media[0] as { updatedAt?: string } | undefined;
          if (m0?.updatedAt) {
            parts.push(`uat:${m0.updatedAt}`);
          }
          parts.push(`med:${JSON.stringify(data.media).slice(0, 400)}`);
        }
        fingerprint = parts.join('|');
      } catch {
        fingerprint = '';
      }
      return `${eventType}_${data.id}_${fingerprint}`;
    }

    if (eventType === 'hazard_tagged' && data.id) {
      const timestamp = data.timestamp ? `_${data.timestamp}` : `_${Date.now()}`;
      return `${eventType}_${data.id}_${timestamp}`;
    }
    
    // For comment updates, include content hash to make each update unique
    if (eventType === 'comment_updated' && data.id) {
      const contentHash = data.content ? `c:${data.content.substring(0, 32)}` : '';
      let mediaPart = '';
      try {
        if (Array.isArray(data.image_data)) {
          mediaPart += `|img:${JSON.stringify(data.image_data).slice(0, 200)}`;
        }
        if (Array.isArray(data.media) && data.media.length > 0) {
          const m0c = data.media[0] as { updatedAt?: string } | undefined;
          if (m0c?.updatedAt) {
            mediaPart += `|uat:${m0c.updatedAt}`;
          }
          mediaPart += `|med:${JSON.stringify(data.media).slice(0, 400)}`;
        }
      } catch {
        mediaPart = '';
      }
      return `${eventType}_${data.id}|${contentHash}${mediaPart}`;
    }
    
    // For other events, use the original logic
    if (data.id) {
      return `${eventType}_${data.id}`;
    }
    if (data.hazard_id) {
      return `${eventType}_${data.hazard_id}`;
    }
    if (data.comment_id) {
      return `${eventType}_${data.comment_id}`;
    }
    // Fallback to timestamp-based ID
    return `${eventType}_${Date.now()}_${Math.random()}`;
  }

  // Generate a unique action ID for tracking
  private generateActionId(): string {
    return `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private normalizeTenantValue(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const normalized = value.trim().toLowerCase();
    return normalized.length > 0 ? normalized : null;
  }

  private setExpectedTenantContext(connectionInfo: NegotiateResponse): void {
    this.expectedTenantSubdomain = this.normalizeTenantValue(connectionInfo.tenantSubdomain || connectionInfo.hub);
    this.expectedTenantId = this.normalizeTenantValue(connectionInfo.tenantId);
  }

  public isTenantScopedEvent(eventType: string): boolean {
    return eventType === 'hazard_created'
      || eventType === 'hazard_updated'
      || eventType === 'hazard_deleted'
      || eventType === 'hazard_tagged'
      || eventType === 'comment_created'
      || eventType === 'comment_updated'
      || eventType === 'comment_deleted'
      || eventType === 'reaction_added'
      || eventType === 'reaction_removed'
      || eventType === 'notification';
  }

  private shouldDropCrossTenantEvent(event: EventStreamEnvelope): boolean {
    if (!this.isTenantScopedEvent(event.type)) return false;
    if (!this.expectedTenantSubdomain && !this.expectedTenantId) return false;

    const eventTenantSubdomain = this.normalizeTenantValue(
      event.tenantSubdomain ?? event.data?.tenantSubdomain ?? event.data?.tenant_subdomain
    );
    const eventTenantId = this.normalizeTenantValue(
      event.tenantId ?? event.data?.tenantId ?? event.data?.tenant_id
    );

    if (!eventTenantSubdomain && !eventTenantId) {
      return true;
    }

    if (this.expectedTenantSubdomain && eventTenantSubdomain && this.expectedTenantSubdomain !== eventTenantSubdomain) {
      return true;
    }

    if (this.expectedTenantId && eventTenantId && this.expectedTenantId !== eventTenantId) {
      return true;
    }

    return false;
  }

  // Track a pending action
  private trackAction(actionId: string, actionType: string, resolve?: (value: any) => void, reject?: (error: any) => void): void {
    this.pendingActions.set(actionId, {
      actionType,
      timestamp: Date.now(),
      resolve,
      reject
    });

    // Log action tracking
    console.log('WEBSOCKET ⏳ Action tracking: pending', {
      actionId,
      actionType,
      status: 'pending',
      timestamp: new Date().toISOString()
    });

    // Set timeout for action
    setTimeout(() => {
      if (this.pendingActions.has(actionId)) {
        const action = this.pendingActions.get(actionId);
        this.pendingActions.delete(actionId);
        
        console.warn('WEBSOCKET ⏰ Action timeout:', {
          actionId,
          actionType: action?.actionType,
          timeout: this.actionTimeout
        });
        
        if (action?.reject) {
          action.reject(new Error(`Action ${actionType} timed out after ${this.actionTimeout}ms`));
        }
      }
    }, this.actionTimeout);
  }

  // Complete a pending action
  private completeAction(actionId: string, success: boolean, data?: any, error?: any): void {
    const action = this.pendingActions.get(actionId);
    if (action) {
      this.pendingActions.delete(actionId);
      
      const status = success ? 'completed' : 'failed';
      const emoji = success ? '✅' : '❌';
      
      console.log(`WEBSOCKET ${emoji} Action completed:`, {
        actionId,
        actionType: action.actionType,
        status,
        success,
        data,
        error,
        timestamp: new Date().toISOString()
      });

      if (success && action.resolve) {
        action.resolve(data);
      } else if (!success && action.reject) {
        action.reject(error || new Error(`Action ${action.actionType} failed`));
      }
    }
  }

  // Setup online/offline event listeners
  private setupOnlineOfflineListeners(): void {
    window.addEventListener('online', () => {
      console.log('Browser came online, reconnecting to realtime...');
      this.connect();
    });

    window.addEventListener('offline', () => {
      console.log('Browser went offline, disconnecting realtime...');
      this.disconnect();
    });

    // Listen for page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        // Page became visible and we're online, ensure connection
        if (!this.isConnected && !this.isConnecting) {
          console.log('Page became visible, reconnecting to realtime...');
          this.connect();
        }
      }
    });
  }

  // Initialize the service
  public initialize(): void {
    if (this.isInitialized) {
      console.log('EventStreamService already initialized');
      return;
    }
    
    console.log('Initializing EventStreamService...');
    this.isInitialized = true;
    
    if (navigator.onLine) {
      console.log('Browser is online, starting realtime connection...');
      this.connect();
    } else {
      console.log('Browser is offline, will connect when online');
    }
  }

  // Connect to Socket.IO server
  public async connect(): Promise<void> {
    const timestamp = new Date().toISOString();

    if (this.isConnecting || (this.socket && this.socket.connected)) {
      console.log('SOCKET.IO connection skipped:', {
        isConnecting: this.isConnecting,
        connected: this.socket?.connected,
        timestamp,
      });
      return;
    }

    if (!navigator.onLine) {
      console.log('SOCKET.IO cannot connect while offline:', {
        isOnline: navigator.onLine,
        timestamp,
      });
      return;
    }

    this.isConnecting = true;
    try {
      await this.disconnect();

      const negotiateResponse = await fetchWithAuth(`${API_BASE_URL}/api/negotiate`, {
        method: 'POST',
      });

      if (!negotiateResponse.ok) {
        if (negotiateResponse.status === 404) {
          throw new Error('Negotiate endpoint not available');
        }
        throw new Error(`Failed to negotiate realtime connection: ${negotiateResponse.statusText}`);
      }

      const connectionInfo: NegotiateResponse = await negotiateResponse.json();
      this.setExpectedTenantContext(connectionInfo);

      const clientUrl = connectionInfo.clientUrl;
      if (!clientUrl) throw new Error('Missing clientUrl from negotiate');

      const accessToken = connectionInfo.accessToken || getAccessToken();
      if (!accessToken) {
        throw new Error('Missing access token for Socket.IO auth');
      }

      this.socket = io(clientUrl, {
        auth: {
          token: accessToken,
          tenantSubdomain: connectionInfo.tenantSubdomain || connectionInfo.hub,
          tenantId: connectionInfo.tenantId,
        },
        transports: ['websocket', 'polling'],
        reconnection: false,
        autoConnect: true,
      });

      this.setupSocketListeners();

      await new Promise<void>((resolve, reject) => {
        if (!this.socket) return reject(new Error('Socket not created'));

        const browser = this.detectBrowser();
        const timeoutMs = browser.isChrome ? 15000 : 10000;

        const timeout = setTimeout(() => {
          cleanup();
          reject(new Error(`Socket.IO connection timeout after ${timeoutMs}ms`));
        }, timeoutMs);

        const onConnect = () => {
          cleanup();
          resolve();
        };
        const onConnectError = (err: Error) => {
          cleanup();
          reject(err);
        };

        const cleanup = () => {
          clearTimeout(timeout);
          this.socket?.off('connect', onConnect);
          this.socket?.off('connect_error', onConnectError);
        };

        this.socket.on('connect', onConnect);
        this.socket.on('connect_error', onConnectError);
      });

      this.isConnected = true;
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.reconnectInterval = 1000;
      this.notifyConnectionListeners(true);
    } catch (error) {
      this.isConnecting = false;
      this.isConnected = false;
      this.notifyConnectionListeners(false);
      this.scheduleReconnect();
    }
  }

  // Setup Socket.IO listeners
  private setupSocketListeners(): void {
    if (!this.socket) return;

    this.socket.on('disconnect', () => {
      this.sawDisconnect = true;
      this.isConnected = false;
      this.isConnecting = false;
      this.notifyConnectionListeners(false);
      this.scheduleReconnect();
    });

    this.socket.on('connect', () => {
      this.isConnected = true;
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.processedEvents.clear();
      this.startHeartbeat();
      this.notifyConnectionListeners(true);
      if (this.sawDisconnect) {
        this.triggerReconnectCatchUp();
      }
    });

    this.socket.on('connect_error', () => {
      this.isConnected = false;
      this.isConnecting = false;
      this.notifyConnectionListeners(false);
    });

    this.socket.on('message', (payload: EventStreamEnvelope) => {
      try {
        let parsed: EventStreamEnvelope = payload;
        if (typeof parsed === 'string') {
          parsed = JSON.parse(parsed);
        }
        this.handleEvent(parsed);
      } catch (err) {
        console.error('SOCKET.IO failed to parse message event:', err);
      }
    });

    this.socket.on('pong', () => {
      this.lastHeartbeatResponse = Date.now();
    });
  }


  // Handle incoming events (fallback for generic events)
  private handleEvent(event: EventStreamEnvelope): void {
    const timestamp = new Date().toISOString();
    const eventId = this.generateEventId(event.type, event.data, event.eventId);
    
    debugLog('WEBSOCKET 🔄 Processing WebSocket event:', {
      type: event.type,
      eventId: eventId,
      data: event.data,
      timestamp: timestamp,
      resolvedStatus: event.data?.resolved,
      hazardId: event.data?.id
    });

    if (this.shouldDropCrossTenantEvent(event)) {
      console.warn('WEBSOCKET 🚨 Ignoring cross-tenant or untagged tenant-scoped event:', {
        type: event.type,
        expectedTenantSubdomain: this.expectedTenantSubdomain,
        expectedTenantId: this.expectedTenantId,
        eventTenantSubdomain: event.tenantSubdomain ?? event.data?.tenantSubdomain ?? event.data?.tenant_subdomain,
        eventTenantId: event.tenantId ?? event.data?.tenantId ?? event.data?.tenant_id,
      });
      return;
    }

    // Check for duplicate events
    if (this.isEventDuplicate(event.type, eventId)) {
      debugLog('WEBSOCKET 🚫 Duplicate event detected, skipping:', {
        type: event.type,
        eventId: eventId,
        timestamp: timestamp
      });
      return;
    }

    debugLog('WEBSOCKET ✅ Processing unique event:', {
      type: event.type,
      eventId: eventId,
      timestamp: timestamp
    });

    switch (event.type) {
      case 'connected':
        debugLog('WEBSOCKET 🔗 Realtime channel connected:', {
          connectionId: event.data.connection_id,
          timestamp: timestamp
        });
        break;

      case 'hazard_created':
        debugLog('WEBSOCKET 📝 Hazard created event:', {
          hazardId: event.data.id,
          timestamp: timestamp
        });
        eventEmitter.emit(EVENTS.HAZARD_ADDED, event.data);
        this.scheduleFeedPrefix();
        break;

      case 'hazard_updated':
        debugLog('WEBSOCKET 📝 Hazard updated event:', {
          hazardId: event.data.id,
          timestamp: timestamp
        });
        if (typeof event.data?.id === 'string' && typeof event.data.resolved === 'boolean') {
          noteHazardResolved(event.data.id, event.data.resolved);
        }
        eventEmitter.emit(EVENTS.HAZARD_UPDATED, event.data);
        this.scheduleFeedPrefix();
        break;

      case 'hazard_deleted':
        debugLog('WEBSOCKET 📝 Hazard deleted event:', {
          hazardId: event.data.id,
          timestamp: timestamp
        });
        eventEmitter.emit(EVENTS.HAZARD_DELETED, event.data.id, event.data);
        break;

      case 'hazard_tagged':
        debugLog('WEBSOCKET 🏷️ Hazard tagged event:', {
          hazardId: event.data.id,
          tags: event.data.tags,
          timestamp: timestamp
        });
        eventEmitter.emit(EVENTS.HAZARD_TAGGED, event.data);
        this.scheduleFeedPrefix();
        break;

      case 'comment_created':
        debugLog('WEBSOCKET 💬 Comment created event received:', {
          commentId: event.data.id,
          hazardId: event.data.incident_id || event.data.hazard_id,
          commentText: event.data.comment?.substring(0, 50),
          username: event.data.username,
          timestamp: timestamp,
          fullData: event.data
        });
        // Ensure the comment data has all required fields
        const commentData = {
          id: event.data.id,
          incident_id: event.data.incident_id || event.data.hazard_id,
          user_id: event.data.user_id,
          username: event.data.username,
          profileImageUrl: event.data.profileImageUrl || event.data.profile_image_url,
          comment: event.data.comment || event.data.text || '',
          created_at: event.data.created_at,
          updated_at: event.data.updated_at || event.data.created_at,
          deleted: event.data.deleted || false,
          image_data: event.data.image_data || [],
          image_count: event.data.image_count || (event.data.image_data?.length || 0)
        };
        debugLog('WEBSOCKET 💬 Emitting COMMENT_ADDED with normalized data:', commentData);
        eventEmitter.emit(EVENTS.COMMENT_ADDED, commentData);
        this.scheduleFeedPrefix();
        break;

      case 'comment_updated':
        debugLog('WEBSOCKET 💬 Comment updated event:', {
          commentId: event.data.id,
          hazardId: event.data.incident_id || event.data.hazard_id,
          timestamp: timestamp
        });
        eventEmitter.emit(EVENTS.COMMENT_UPDATED, event.data);
        this.scheduleFeedPrefix();
        break;

      case 'comment_deleted':
        debugLog('WEBSOCKET 💬 Comment deleted event:', {
          commentId: event.data.id,
          hazardId: event.data.incident_id || event.data.hazard_id,
          timestamp: timestamp
        });
        // Extract incident_id and comment id for the deletion event
        const incidentId = event.data.incident_id || event.data.hazard_id;
        const commentId = event.data.id;
        if (incidentId && commentId) {
          eventEmitter.emit(EVENTS.COMMENT_DELETED, incidentId, commentId);
          this.scheduleFeedPrefix();
        }
        break;

      case 'reaction_added':
        debugLog('WEBSOCKET 👍 Reaction added event:', {
          contentType: event.data.content_type,
          contentId: event.data.content_id,
          reactionType: event.data.reaction_type_name,
          timestamp: timestamp
        });
        if (event.data.content_type === 'card') {
          eventEmitter.emit(EVENTS.REACTION_ADDED, event.data.content_id, event.data);
        } else if (event.data.content_type === 'comment') {
          eventEmitter.emit(EVENTS.REACTION_ADDED, event.data.content_id, event.data);
        }
        this.scheduleFeedPrefix();
        break;

      case 'reaction_removed':
        debugLog('WEBSOCKET 👎 Reaction removed event:', {
          contentType: event.data.content_type,
          contentId: event.data.content_id,
          reactionType: event.data.reaction_type_name,
          timestamp: timestamp
        });
        if (event.data.content_type === 'card') {
          eventEmitter.emit(EVENTS.REACTION_DELETED, event.data.content_id, event.data);
        } else if (event.data.content_type === 'comment') {
          eventEmitter.emit(EVENTS.REACTION_DELETED, event.data.content_id, event.data);
        }
        this.scheduleFeedPrefix();
        break;

      case 'action_processed':
        // Deprecated - actions now use HTTP APIs directly
        debugLog('WEBSOCKET ✅ Action processed event (deprecated):', {
          actionId: event.data.action_id,
          actionType: event.data.action_type,
          success: event.data.success,
          objectId: event.data.object_id,
          timestamp: timestamp
        });
        // No longer needed since we use HTTP APIs directly
        break;

      case 'notification':
        debugLog('WEBSOCKET 🔔 Notification received via send_to_user:', {
          notification: event.data,
          timestamp: timestamp
        });
        // Emit notification event for UI components to handle
        eventEmitter.emit(EVENTS.NOTIFICATION_RECEIVED, event.data);
        break;

      default:
        debugLog('WEBSOCKET ❓ Unknown event type:', {
          type: event.type,
          data: event.data,
          timestamp: timestamp
        });
    }
  }

  // Disconnect from Socket.IO
  public async disconnect(): Promise<void> {
    if (this.isConnected || this.socket?.connected) {
      this.sawDisconnect = true;
    }
    this.isConnected = false;
    this.isConnecting = false;
    this.notifyConnectionListeners(false);

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.stopHeartbeat();
    this.expectedTenantSubdomain = null;
    this.expectedTenantId = null;

    if (this.socket) {
      try {
        this.socket.removeAllListeners();
        this.socket.disconnect();
      } catch (error) {
        console.error('Error disconnecting Socket.IO:', error);
      }
      this.socket = null;
    }
  }

  // Schedule reconnection attempt
  private scheduleReconnect(): void {
    if (this.reconnectTimer || !navigator.onLine || this.isConnecting) {
      return; // Reconnect already scheduled, offline, or currently connecting
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn('EventStreamService: max reconnect attempts reached, resuming polling fallback');
      this.isConnected = false;
      this.notifyConnectionListeners(false);
      return;
    }

    this.reconnectAttempts++;
    
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (navigator.onLine && !this.isConnecting) {
        this.connect();
      }
    }, this.reconnectInterval);

    // Exponential backoff with jitter
    this.reconnectInterval = Math.min(
      this.reconnectInterval * 2 + Math.random() * 1000,
      this.maxReconnectInterval
    );
  }

  // Add connection listener
  public addConnectionListener(listener: (connected: boolean) => void): void {
    this.connectionListeners.push(listener);
  }

  // Remove connection listener
  public removeConnectionListener(listener: (connected: boolean) => void): void {
    const index = this.connectionListeners.indexOf(listener);
    if (index > -1) {
      this.connectionListeners.splice(index, 1);
    }
  }

  // Notify connection listeners
  private notifyConnectionListeners(connected: boolean): void {
    this.connectionListeners.forEach(listener => {
      try {
        listener(connected);
      } catch (error) {
        console.error('Error in connection listener:', error);
      }
    });
  }

  /** Debounced prefix refresh. Does not write Dexie itself. */
  private scheduleFeedPrefix(): void {
    void import('./feedSync').then(({ request }) => {
      request('prefix').catch((error: unknown) => {
        console.error('EventStreamService: feed prefix refresh failed:', error);
      });
    });
  }

  /** Fetch community hazards missed during disconnect (dynamic import avoids a cycle). */
  private triggerReconnectCatchUp(): void {
    this.scheduleFeedPrefix();
  }

  // Get connection status
  public isConnectedToStream(): boolean {
    return this.isConnected && !!this.socket?.connected;
  }

  // Manual reconnect
  public async reconnect(): Promise<void> {
    console.log('Manual reconnect requested');
    this.reconnectAttempts = 0;
    this.reconnectInterval = 1000;
    await this.connect();
  }

  // Send message via WebSocket with detailed logging
  // NOTE: This method is deprecated - clients should call HTTP APIs directly
  public async sendMessage(methodName: string, args?: any): Promise<void> {
    console.warn('WEBSOCKET 📤 sendMessage is deprecated - use HTTP APIs directly instead of websocket messages');
    // Use HTTP API directly
    await this.sendMessageViaHTTP(methodName, args);
  }

  // HTTP fallback method for when WebSocket is not available
  private async sendMessageViaHTTP(methodName: string, args?: any): Promise<any> {
    const timestamp = new Date().toISOString();
    
    console.log('WEBSOCKET 🌐 HTTP fallback for WebSocket message:', {
      method: methodName,
      arguments: args,
      timestamp: timestamp
    });

    try {
      // Map WebSocket methods to HTTP endpoints
      let endpoint: string;
      let payload: any = args || {};
      
      switch (methodName) {
        case 'CreateHazard':
          endpoint = `${API_BASE_URL}/api/handleSubmit`;
          break;
        case 'DeleteHazard':
          endpoint = `${API_BASE_URL}/api/softDeleteHazard`;
          payload = { id: args?.hazard_id };
          break;
        case 'CreateComment':
          endpoint = `${API_BASE_URL}/api/submitComment`;
          break;
        case 'DeleteComment':
          endpoint = `${API_BASE_URL}/api/softDeleteComment`;
          payload = { id: args?.comment_id };
          break;
        case 'AddHazardReaction':
          endpoint = `${API_BASE_URL}/api/addHazardReaction`;
          break;
        case 'RemoveHazardReaction':
          endpoint = `${API_BASE_URL}/api/removeHazardReaction`;
          break;
        case 'AddCommentReaction':
          endpoint = `${API_BASE_URL}/api/addCommentReaction`;
          break;
        case 'RemoveCommentReaction':
          endpoint = `${API_BASE_URL}/api/removeCommentReaction`;
          break;
        default:
          throw new Error(`Unknown method for HTTP fallback: ${methodName}`);
      }

      const response = await fetchWithAuth(endpoint, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP fallback failed: ${response.statusText}`);
      }

      const result = await response.json();
      
      console.log('WEBSOCKET 🌐 HTTP fallback successful:', {
        method: methodName,
        endpoint: endpoint,
        result: result,
        timestamp: timestamp
      });

      return result;
    } catch (error) {
      console.error('WEBSOCKET 🌐 HTTP fallback failed:', {
        method: methodName,
        error: error,
        timestamp: timestamp
      });
      throw error;
    }
  }

  // Action methods for hazards
  public async createHazard(hazardData: any): Promise<any> {
    const actionId = this.generateActionId();
    console.log('API 📤 Creating hazard via HTTP API:', {
      actionId,
      hazardData: hazardData,
      timestamp: new Date().toISOString()
    });
    
    try {
      const result = await this.sendMessageViaHTTP('CreateHazard', hazardData);
      console.log('API ✅ Hazard created successfully:', {
        actionId,
        result: result,
        timestamp: new Date().toISOString()
      });
      return {
        action_id: actionId,
        action_type: 'CreateHazard',
        success: true,
        object_id: result.id,
        data: result,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('API ❌ Hazard creation failed:', {
        actionId,
        error: error,
        timestamp: new Date().toISOString()
      });
      return {
        action_id: actionId,
        action_type: 'CreateHazard',
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  public async deleteHazard(hazardId: string): Promise<any> {
    const actionId = this.generateActionId();
    console.log('API 📤 Deleting hazard via HTTP API:', {
      actionId,
      hazardId: hazardId,
      timestamp: new Date().toISOString()
    });
    
    try {
      const result = await this.sendMessageViaHTTP('DeleteHazard', { hazard_id: hazardId });
      console.log('API ✅ Hazard deleted successfully:', {
        actionId,
        result: result,
        timestamp: new Date().toISOString()
      });
      return {
        action_id: actionId,
        action_type: 'DeleteHazard',
        success: true,
        object_id: hazardId,
        data: result,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('API ❌ Hazard deletion failed:', {
        actionId,
        error: error,
        timestamp: new Date().toISOString()
      });
      return {
        action_id: actionId,
        action_type: 'DeleteHazard',
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Action methods for comments
  public async createComment(commentData: any): Promise<any> {
    const actionId = this.generateActionId();
    console.log('API 📤 Creating comment via HTTP API:', {
      actionId,
      commentData: commentData,
      timestamp: new Date().toISOString()
    });
    
    try {
      const result = await this.sendMessageViaHTTP('CreateComment', commentData);
      console.log('API ✅ Comment created successfully:', {
        actionId,
        result: result,
        timestamp: new Date().toISOString()
      });
      return {
        action_id: actionId,
        action_type: 'CreateComment',
        success: true,
        object_id: result.id,
        data: result,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('API ❌ Comment creation failed:', {
        actionId,
        error: error,
        timestamp: new Date().toISOString()
      });
      return {
        action_id: actionId,
        action_type: 'CreateComment',
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  public async deleteComment(commentId: string): Promise<any> {
    const actionId = this.generateActionId();
    console.log('API 📤 Deleting comment via HTTP API:', {
      actionId,
      commentId: commentId,
      timestamp: new Date().toISOString()
    });
    
    try {
      const result = await this.sendMessageViaHTTP('DeleteComment', { comment_id: commentId });
      console.log('API ✅ Comment deleted successfully:', {
        actionId,
        result: result,
        timestamp: new Date().toISOString()
      });
      return {
        action_id: actionId,
        action_type: 'DeleteComment',
        success: true,
        object_id: commentId,
        data: result,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('API ❌ Comment deletion failed:', {
        actionId,
        error: error,
        timestamp: new Date().toISOString()
      });
      return {
        action_id: actionId,
        action_type: 'DeleteComment',
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Action methods for reactions
  public async addHazardReaction(reactionData: any): Promise<any> {
    const actionId = this.generateActionId();
    console.log('API 📤 Adding hazard reaction via HTTP API:', {
      actionId,
      reactionData: reactionData,
      timestamp: new Date().toISOString()
    });
    
    try {
      const result = await this.sendMessageViaHTTP('AddHazardReaction', reactionData);
      console.log('API ✅ Hazard reaction added successfully:', {
        actionId,
        result: result,
        timestamp: new Date().toISOString()
      });
      return {
        action_id: actionId,
        action_type: 'AddHazardReaction',
        success: true,
        object_id: result.reactionId,
        data: result,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('API ❌ Hazard reaction addition failed:', {
        actionId,
        error: error,
        timestamp: new Date().toISOString()
      });
      return {
        action_id: actionId,
        action_type: 'AddHazardReaction',
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  public async removeHazardReaction(reactionData: any): Promise<any> {
    const actionId = this.generateActionId();
    console.log('API 📤 Removing hazard reaction via HTTP API:', {
      actionId,
      reactionData: reactionData,
      timestamp: new Date().toISOString()
    });
    
    try {
      const result = await this.sendMessageViaHTTP('RemoveHazardReaction', reactionData);
      console.log('API ✅ Hazard reaction removed successfully:', {
        actionId,
        result: result,
        timestamp: new Date().toISOString()
      });
      return {
        action_id: actionId,
        action_type: 'RemoveHazardReaction',
        success: true,
        object_id: reactionData.id,
        data: result,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('API ❌ Hazard reaction removal failed:', {
        actionId,
        error: error,
        timestamp: new Date().toISOString()
      });
      return {
        action_id: actionId,
        action_type: 'RemoveHazardReaction',
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  public async addCommentReaction(reactionData: any): Promise<any> {
    const actionId = this.generateActionId();
    console.log('API 📤 Adding comment reaction via HTTP API:', {
      actionId,
      reactionData: reactionData,
      timestamp: new Date().toISOString()
    });
    
    try {
      const result = await this.sendMessageViaHTTP('AddCommentReaction', reactionData);
      console.log('API ✅ Comment reaction added successfully:', {
        actionId,
        result: result,
        timestamp: new Date().toISOString()
      });
      return {
        action_id: actionId,
        action_type: 'AddCommentReaction',
        success: true,
        object_id: result.reactionId,
        data: result,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('API ❌ Comment reaction addition failed:', {
        actionId,
        error: error,
        timestamp: new Date().toISOString()
      });
      return {
        action_id: actionId,
        action_type: 'AddCommentReaction',
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  public async removeCommentReaction(reactionData: any): Promise<any> {
    const actionId = this.generateActionId();
    console.log('API 📤 Removing comment reaction via HTTP API:', {
      actionId,
      reactionData: reactionData,
      timestamp: new Date().toISOString()
    });
    
    try {
      const result = await this.sendMessageViaHTTP('RemoveCommentReaction', reactionData);
      console.log('API ✅ Comment reaction removed successfully:', {
        actionId,
        result: result,
        timestamp: new Date().toISOString()
      });
      return {
        action_id: actionId,
        action_type: 'RemoveCommentReaction',
        success: true,
        object_id: reactionData.reactionId,
        data: result,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('API ❌ Comment reaction removal failed:', {
        actionId,
        error: error,
        timestamp: new Date().toISOString()
      });
      return {
        action_id: actionId,
        action_type: 'RemoveCommentReaction',
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Generic action sender with error handling and retry logic
  public async sendAction(_actionType: string, _payload: any): Promise<boolean> {
    console.warn('sendAction not supported in Socket.IO (service-only) mode');
    return false;
  }

  // Request-response methods for GET operations via realtime (deprecated; use HTTP services)
  private pendingRequests = new Map<string, { resolve: (value: any) => void; reject: (error: any) => void; timeout: NodeJS.Timeout }>();

  // Setup response handlers in the hub listeners
  private setupResponseHandlers(): void {}

  // Generic request-response method
  private async sendRequest<T>(_method: string, _params: any = {}, _timeoutMs: number = 10000): Promise<T> {
    throw new Error('Socket.IO request/response not supported; use HTTP services instead');
  }

  // GET methods using realtime request-response pattern (deprecated)
  public async getUserHazards(since?: number): Promise<any[]> {
    const params = since ? { since } : {};
    return this.sendRequest('GetUserIncidents', params);
  }

  public async getAllHazards(limit?: number, offset?: number): Promise<any[]> {
    const params: any = {};
    if (limit !== undefined) params.limit = limit;
    if (offset !== undefined) params.offset = offset;
    return this.sendRequest('GetAllIncidents', params);
  }

  public async getCommentsForHazard(hazardId: string, since?: number): Promise<any[]> {
    const params: any = { hazardId };
    if (since !== undefined) params.since = since;
    return this.sendRequest('GetCommentsForHazard', params);
  }

  public async getHazardReactions(hazardId: string): Promise<any[]> {
    return this.sendRequest('GetHazardReactions', { hazardId });
  }

  public async getCommentReactions(commentId: string): Promise<any[]> {
    return this.sendRequest('GetCommentReactions', { commentId });
  }

  public async getBatchHazardReactions(hazardIds: string[]): Promise<Record<string, any[]>> {
    return this.sendRequest('GetBatchHazardReactions', { ids: hazardIds });
  }

  public async getBatchCommentReactions(commentIds: string[]): Promise<Record<string, any[]>> {
    return this.sendRequest('GetBatchCommentReactions', { ids: commentIds });
  }

  // Utility method to check if realtime requests are preferred
  public shouldUseRealtimeForRequests(): boolean {
    return false;
  }

  // Cleanup on service destruction
  public async destroy(): Promise<void> {
    await this.disconnect();
    this.connectionListeners = [];
    
    // Cleanup pending requests
    this.pendingRequests.forEach(({ reject, timeout }) => {
      clearTimeout(timeout);
      reject(new Error('Service destroyed'));
    });
    this.pendingRequests.clear();
    
    // Cleanup pending actions
    this.pendingActions.forEach(({ reject }) => {
      if (reject) {
        reject(new Error('Service destroyed'));
      }
    });
    this.pendingActions.clear();
    
    // Clear deduplication cache
    this.processedEvents.clear();
    
    window.removeEventListener('online', this.connect);
    window.removeEventListener('offline', this.disconnect);
    document.removeEventListener('visibilitychange', this.connect);
  }

  // Client-side JWT generation removed; tokens now minted by server negotiate endpoint

  // Public method to clear deduplication cache if needed
  public clearEventCache(): void {
    this.processedEvents.clear();
    console.log('📡 EventStreamService: Manually cleared event deduplication cache');
  }

  // Public method to get cache statistics for debugging
  public getCacheStats(): { totalEvents: number; eventsByType: Record<string, number> } {
    const eventsByType: Record<string, number> = {};
    let totalEvents = 0;
    
    this.processedEvents.forEach((eventIds, eventType) => {
      const count = eventIds.size;
      eventsByType[eventType] = count;
      totalEvents += count;
    });
    
    return { totalEvents, eventsByType };
  }
}

// Create singleton instance
const eventStreamService = new EventStreamService();

// Note: Don't auto-initialize here, let the App component control initialization
console.log('EventStreamService singleton created');

// Expose debugging methods to window for console access
if (typeof window !== 'undefined') {
  (window as any).eventStreamService = eventStreamService;
  console.log('🔧 EventStreamService debugging methods available:');
  console.log('  - window.eventStreamService.clearEventCache() - Clear event deduplication cache');
  console.log('  - window.eventStreamService.getCacheStats() - Get cache statistics');
}

export default eventStreamService;